import React, { useEffect, useState } from 'react';
import { metricsApi, bodyMeasurementsApi, nutritionApi } from '../api/client';
import type { PersonalMetrics, BodyMeasurement, NutritionEntry } from '../api/client';
import './Health.css';

const Health: React.FC = () => {
  const [metrics, setMetrics] = useState<PersonalMetrics[]>([]);
  const [bodyMeasurements, setBodyMeasurements] = useState<BodyMeasurement[]>([]);
  const [nutritionEntries, setNutritionEntries] = useState<NutritionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'metrics' | 'body' | 'nutrition'>('metrics');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchHealthData();
  }, []);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const [metricsResponse, bodyResponse, nutritionResponse] = await Promise.all([
        metricsApi.getMetrics(),
        bodyMeasurementsApi.getBodyMeasurements(),
        nutritionApi.getNutritionEntries()
      ]);
      
      setMetrics(metricsResponse.data);
      setBodyMeasurements(bodyResponse.data);
      setNutritionEntries(nutritionResponse.data);
    } catch (err) {
      setError('Failed to load health data');
      console.error('Health data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLatestMetrics = () => {
    if (metrics.length === 0) return null;
    return metrics.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  };

  const getWeightTrend = () => {
    if (metrics.length < 2) return null;
    const sorted = [...metrics].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const latest = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];
    
    if (!latest.weight || !previous.weight) return null;
    
    const diff = latest.weight.value - previous.weight.value;
    return {
      change: diff,
      trend: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable'
    };
  };

  const calculateAverageCalories = () => {
    if (metrics.length === 0) return null;
    const metricsWithCalories = metrics.filter(m => m.calories?.consumed);
    if (metricsWithCalories.length === 0) return null;
    
    const total = metricsWithCalories.reduce((sum, m) => sum + (m.calories?.consumed || 0), 0);
    return Math.round(total / metricsWithCalories.length);
  };

  const getHealthScore = () => {
    const latest = getLatestMetrics();
    if (!latest) return null;
    
    let score = 0;
    let factors = 0;

    // Sleep quality (0-25 points)
    if (latest.sleep) {
      factors++;
      switch (latest.sleep.quality) {
        case 'excellent': score += 25; break;
        case 'good': score += 20; break;
        case 'fair': score += 10; break;
        case 'poor': score += 0; break;
      }
    }

    // Steps (0-25 points)
    if (latest.steps) {
      factors++;
      if (latest.steps >= 10000) score += 25;
      else if (latest.steps >= 7500) score += 20;
      else if (latest.steps >= 5000) score += 15;
      else if (latest.steps >= 2500) score += 10;
      else score += 5;
    }

    // Heart rate (0-25 points)
    if (latest.heartRate?.resting) {
      factors++;
      const resting = latest.heartRate.resting;
      if (resting >= 60 && resting <= 70) score += 25;
      else if (resting >= 50 && resting <= 80) score += 20;
      else if (resting >= 40 && resting <= 90) score += 15;
      else score += 10;
    }

    // Hydration (0-25 points)
    if (latest.hydration?.amount) {
      factors++;
      const hydration = latest.hydration.amount;
      if (hydration >= 64) score += 25;
      else if (hydration >= 48) score += 20;
      else if (hydration >= 32) score += 15;
      else score += 10;
    }

    return factors > 0 ? Math.round(score / factors) : null;
  };

  if (loading) {
    return <div className="health-page"><div className="loading">Loading health data...</div></div>;
  }

  if (error) {
    return <div className="health-page"><div className="error">Error: {error}</div></div>;
  }

  const latestMetrics = getLatestMetrics();
  const weightTrend = getWeightTrend();
  const avgCalories = calculateAverageCalories();
  const healthScore = getHealthScore();

  return (
    <div className="health-page">
      <header className="page-header">
        <h1>❤️ Health Dashboard</h1>
      </header>

      {error && <div className="error-message">{error}</div>}

      {/* Health Overview Cards */}
      <div className="health-overview">
        <div className="health-card">
          <div className="health-card-icon">💪</div>
          <div className="health-card-content">
            <h3>Health Score</h3>
            <div className="health-score">
              {healthScore ? (
                <>
                  <span className="score-value">{healthScore}</span>
                  <span className="score-max">/25</span>
                </>
              ) : (
                <span className="no-data">No data</span>
              )}
            </div>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-icon">⚖️</div>
          <div className="health-card-content">
            <h3>Weight</h3>
            <div className="weight-info">
              {latestMetrics?.weight ? (
                <>
                  <span className="weight-value">{latestMetrics.weight.value} {latestMetrics.weight.unit}</span>
                  {weightTrend && (
                    <span className={`weight-trend ${weightTrend.trend}`}>
                      {weightTrend.trend === 'up' ? '↗️' : weightTrend.trend === 'down' ? '↘️' : '➡️'}
                      {Math.abs(weightTrend.change).toFixed(1)} {latestMetrics.weight.unit}
                    </span>
                  )}
                </>
              ) : (
                <span className="no-data">No data</span>
              )}
            </div>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-icon">🚶</div>
          <div className="health-card-content">
            <h3>Steps Today</h3>
            <div className="steps-info">
              {latestMetrics?.steps ? (
                <span className="steps-value">{latestMetrics.steps.toLocaleString()}</span>
              ) : (
                <span className="no-data">No data</span>
              )}
            </div>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-icon">🍽️</div>
          <div className="health-card-content">
            <h3>Avg. Calories</h3>
            <div className="calories-info">
              {avgCalories ? (
                <span className="calories-value">{avgCalories} kcal</span>
              ) : (
                <span className="no-data">No data</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="health-tabs">
        <button 
          className={`tab-button ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          📊 Metrics ({metrics.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'body' ? 'active' : ''}`}
          onClick={() => setActiveTab('body')}
        >
          📏 Body Measurements ({bodyMeasurements.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'nutrition' ? 'active' : ''}`}
          onClick={() => setActiveTab('nutrition')}
        >
          🥗 Nutrition ({nutritionEntries.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="health-content">
        {activeTab === 'metrics' && (
          <div className="metrics-section">
            <div className="section-header">
              <h2>Personal Metrics</h2>
            </div>
            <div className="metrics-grid">
              {metrics
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((metric) => (
                <div key={metric.id} className="metric-card">
                  <div className="metric-date">{new Date(metric.date).toLocaleDateString()}</div>
                  <div className="metric-details">
                    {metric.steps && (
                      <div className="metric-item">
                        <span className="metric-label">🚶 Steps:</span>
                        <span className="metric-value">{metric.steps.toLocaleString()}</span>
                      </div>
                    )}
                    {metric.weight && (
                      <div className="metric-item">
                        <span className="metric-label">⚖️ Weight:</span>
                        <span className="metric-value">{metric.weight.value} {metric.weight.unit}</span>
                      </div>
                    )}
                    {metric.heartRate && (
                      <div className="metric-item">
                        <span className="metric-label">❤️ Heart Rate:</span>
                        <span className="metric-value">
                          Rest: {metric.heartRate.resting} | Avg: {metric.heartRate.average}
                        </span>
                      </div>
                    )}
                    {metric.sleep && (
                      <div className="metric-item">
                        <span className="metric-label">😴 Sleep:</span>
                        <span className="metric-value">
                          {metric.sleep.duration}h ({metric.sleep.quality})
                        </span>
                      </div>
                    )}
                    {metric.hydration && (
                      <div className="metric-item">
                        <span className="metric-label">💧 Hydration:</span>
                        <span className="metric-value">{metric.hydration.amount} {metric.hydration.unit}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {metrics.length === 0 && (
                <div className="no-data-message">No metrics recorded yet</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'body' && (
          <div className="body-section">
            <div className="section-header">
              <h2>Body Measurements</h2>
            </div>
            <div className="body-grid">
              {bodyMeasurements
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((measurement) => (
                <div key={measurement.id} className="body-card">
                  <div className="body-date">{new Date(measurement.date).toLocaleDateString()}</div>
                  <div className="body-details">
                    {measurement.waist && (
                      <div className="body-item">
                        <span className="body-label">Waist:</span>
                        <span className="body-value">{measurement.waist.value} {measurement.waist.unit}</span>
                      </div>
                    )}
                    {measurement.chest && (
                      <div className="body-item">
                        <span className="body-label">Chest:</span>
                        <span className="body-value">{measurement.chest.value} {measurement.chest.unit}</span>
                      </div>
                    )}
                    {measurement.bodyFatPercentage && (
                      <div className="body-item">
                        <span className="body-label">Body Fat:</span>
                        <span className="body-value">{measurement.bodyFatPercentage}%</span>
                      </div>
                    )}
                    {measurement.muscleMass && (
                      <div className="body-item">
                        <span className="body-label">Muscle Mass:</span>
                        <span className="body-value">{measurement.muscleMass.value} {measurement.muscleMass.unit}</span>
                      </div>
                    )}
                    {measurement.notes && (
                      <div className="body-notes">{measurement.notes}</div>
                    )}
                  </div>
                </div>
              ))}
              {bodyMeasurements.length === 0 && (
                <div className="no-data-message">No body measurements recorded yet</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'nutrition' && (
          <div className="nutrition-section">
            <div className="section-header">
              <h2>Nutrition Entries</h2>
            </div>
            <div className="nutrition-grid">
              {nutritionEntries
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((entry) => (
                <div key={entry.id} className="nutrition-card">
                  <div className="nutrition-header">
                    <div className="nutrition-date">{new Date(entry.date).toLocaleDateString()}</div>
                    <div className="meal-type">{entry.mealType}</div>
                  </div>
                  <div className="nutrition-details">
                    <div className="calories-total">{entry.totalCalories} calories</div>
                    <div className="macros">
                      <span className="macro protein">P: {entry.totalMacros.protein}g</span>
                      <span className="macro carbs">C: {entry.totalMacros.carbs}g</span>
                      <span className="macro fat">F: {entry.totalMacros.fat}g</span>
                    </div>
                    <div className="foods-list">
                      {entry.foods.map((food, index) => (
                        <div key={index} className="food-item">
                          {food.name} ({food.quantity} {food.unit})
                        </div>
                      ))}
                    </div>
                    {entry.notes && (
                      <div className="nutrition-notes">{entry.notes}</div>
                    )}
                  </div>
                </div>
              ))}
              {nutritionEntries.length === 0 && (
                <div className="no-data-message">No nutrition entries recorded yet</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Health;